import { get } from 'lodash-es';
import { LEVEL, PAGE_TYPE, SOURCE } from '../constants/logger';

const ORDER_CONFIRM_RE = /^\/orders\/[^/]+\/confirm$/;
const USER_EDIT_RE = /^\/users\/[^/]+\/edit$/;
const LOCALE_PREFIX_RE =
  /^\/(en|zh-hant|zh-hk|zh-tw|zh-cn|vi|ms|ja|th|id|de|fr)\//;
const TRACE_ID_HEADER = 'x-trace-id';

const normalizePathname = (pathname) => {
  const withoutTrailingSlash =
    pathname === '/' ? pathname : pathname.replace(/\/$/, '');
  return withoutTrailingSlash.replace(LOCALE_PREFIX_RE, '/');
};

const getPage = () => {
  try {
    const pathname = normalizePathname(window.location.pathname);

    if (pathname === '/' || pathname === '') return PAGE_TYPE.home;
    if (pathname === '/cart') return PAGE_TYPE.cart;
    if (pathname === '/checkout') return PAGE_TYPE.checkout;
    if (pathname === '/fast_checkout') return PAGE_TYPE.fastCheckout;
    if (ORDER_CONFIRM_RE.test(pathname)) return PAGE_TYPE.confirm;
    if (pathname.startsWith('/one-page-stores/'))
      return PAGE_TYPE.onePageStoreCheckout;
    if (pathname.startsWith('/promotions/')) return PAGE_TYPE.promotions;
    if (pathname === '/products' || pathname.startsWith('/categories'))
      return PAGE_TYPE.plp;
    if (pathname.startsWith('/products/')) return PAGE_TYPE.pdp;
    if (pathname === '/users/sign_in') return PAGE_TYPE.signIn;
    if (pathname === '/users/password/new') return PAGE_TYPE.forgotPassword;
    if (pathname.startsWith('/users/password/')) return PAGE_TYPE.resetPassword;
    if (USER_EDIT_RE.test(pathname)) return PAGE_TYPE.memberCenter;
    if (pathname === '/member_points') return PAGE_TYPE.memberPoints;
    if (pathname === '/user_credits') return PAGE_TYPE.userCredits;
    if (pathname === '/coupons') return PAGE_TYPE.coupons;
    if (pathname === '/coupon_center') return PAGE_TYPE.couponCenter;
    if (pathname === '/messages') return PAGE_TYPE.messages;
    if (pathname === '/orders') return PAGE_TYPE.olp;
    if (pathname.startsWith('/orders/')) return PAGE_TYPE.odp;
    if (pathname === '/wishlist') return PAGE_TYPE.wishlist;
    if (pathname === '/about') return PAGE_TYPE.about;
    if (pathname === '/about/terms') return PAGE_TYPE.terms;
    if (pathname === '/about/policy') return PAGE_TYPE.returnPolicy;
    if (pathname === '/about/delivery-policy') return PAGE_TYPE.deliveryPolicy;
    if (pathname === '/about/privacy-policy') return PAGE_TYPE.privacyPolicy;
    if (pathname === '/about/anti-fraud') return PAGE_TYPE.antiFraud;
    if (pathname === '/blog/posts') return PAGE_TYPE.blog;
    if (pathname.startsWith('/blog/posts/')) return PAGE_TYPE.blogPost;

    // expressCheckout shares /pages/:slug with custom pages and cannot be inferred from URL alone
    return PAGE_TYPE.other;
  } catch (e) {
    if (isSentryLoaded()) {
      window.Sentry.withScope((scope) => {
        scope.setLevel(LEVEL.warn);
        scope.setTag('source', SOURCE.internal);
        window.Sentry.captureException(e);
      });
    }
    return undefined;
  }
};

const isSentryLoaded = () => typeof window.Sentry !== 'undefined';

const toError = (value) => {
  if (value instanceof Error) return value;
  // AngularJS $http / fetch Response rejection: build a readable message from status
  // Use typeof check on status — statusText can be "" on HTTP/2 or network errors
  if (
    value != null &&
    typeof value === 'object' &&
    typeof value.status === 'number'
  ) {
    const label = value.statusText || 'Unknown Error';
    return new Error(`${value.status} ${label}`);
  }
  return new Error(String(value));
};

// Reads status from both axios (error.response.status) and
// AngularJS $http / fetch Response shapes (error.status directly).
const is4xxApiError = (error) => {
  const status = get(error, ['response', 'status']) ?? get(error, ['status']);
  return typeof status === 'number' && status >= 400 && status < 500;
};

const getTraceId = (error) => {
  const headers =
    get(error, ['response', 'headers']) ?? get(error, ['headers']);
  // AngularJS $http rejection exposes headers as a getter function; axios uses a plain object
  const traceId =
    typeof headers === 'function'
      ? headers(TRACE_ID_HEADER)
      : get(headers, [TRACE_ID_HEADER]);
  return typeof traceId === 'string' ? traceId : undefined;
};

// Reads response body from both axios (error.response.data) and
// AngularJS $http / fetch Response shapes (error.data directly).
const getResponseData = (error) => {
  const data = get(error, ['response', 'data']) ?? get(error, ['data']);
  if (data === undefined || data === null) return undefined;
  try {
    return JSON.stringify(data);
  } catch {
    return undefined;
  }
};

const buildContext = (error, context) => {
  const traceId = getTraceId(error);
  const responseData = getResponseData(error);
  const fromError = {
    ...(traceId && { traceId }),
    ...(responseData && { response: responseData }),
  };
  if (Object.keys(fromError).length === 0) return context;
  return { ...context, ...fromError };
};

const sendSentry = (value, { context, tags = {} } = {}, level) => {
  const page = getPage();
  const mergedTags = {
    source: SOURCE.manually,
    ...(page !== undefined && { page }),
    ...tags,
  };

  if (isSentryLoaded()) {
    window.Sentry.withScope((scope) => {
      scope.setLevel(level);
      scope.setTags(mergedTags);

      if (context) {
        scope.setContext('extra', context);
      }

      if (level === LEVEL.info) {
        window.Sentry.captureMessage(String(value));
      } else {
        window.Sentry.captureException(toError(value));
      }
    });
  } else {
    console.error(`[loggerService][${page}]`, value, { context, tags });
  }
};

const loggerService = {
  /**
   * Captures an exception at error level via Sentry captureException.
   * Falls back to console.error when Sentry is not loaded.
   * @param {Error} error
   * @param {{ context?: object, tags?: object }} [options]
   * @param {object} [options.context] - Additional context attached to the Sentry event
   * @param {object} [options.tags] - Custom tags merged into the default scope tags
   */
  error(error, { context, tags } = {}) {
    try {
      if (is4xxApiError(error)) {
        console.log('[loggerService] skip 4xx (error)', error);
        return;
      }
      const mergedContext = buildContext(error, context);
      sendSentry(error, { context: mergedContext, tags }, LEVEL.error);
    } catch (e) {
      // eslint-disable-next-line no-restricted-syntax
      console.error('[loggerService] error failed', e);
    }
  },

  /**
   * Captures an exception at warn level via Sentry captureException.
   * Falls back to console.error when Sentry is not loaded.
   * @param {Error} error
   * @param {{ context?: object, tags?: object }} [options]
   * @param {object} [options.context] - Additional context attached to the Sentry event
   * @param {object} [options.tags] - Custom tags merged into the default scope tags
   */
  warn(error, { context, tags } = {}) {
    try {
      if (is4xxApiError(error)) {
        console.log('[loggerService] skip 4xx (warn)', error);
        return;
      }
      const mergedContext = buildContext(error, context);
      sendSentry(error, { context: mergedContext, tags }, LEVEL.warn);
    } catch (e) {
      // eslint-disable-next-line no-restricted-syntax
      console.error('[loggerService] warn failed', e);
    }
  },

  /**
   * Captures a message at info level via Sentry captureMessage.
   * Falls back to console.error when Sentry is not loaded.
   * @param {string} message
   * @param {{ context?: object, tags?: object }} [options]
   * @param {object} [options.context] - Additional context attached to the Sentry event
   * @param {object} [options.tags] - Custom tags merged into the default scope tags
   */
  info(message, { context, tags } = {}) {
    try {
      sendSentry(message, { context, tags }, LEVEL.info);
    } catch (e) {
      // eslint-disable-next-line no-restricted-syntax
      console.error('[loggerService] info failed', e);
    }
  },
};

export default loggerService;
